import { Token } from './../models/token.model'
import { DateTimeTools } from './date-time-tools'
import { Injectable } from '@angular/core'
import Bowser from 'bowser'

@Injectable({
  providedIn: 'root'
  })
export class UsefulTools {
  constructor (
    private dateTimeTools: DateTimeTools
  ) { }

  /**
   * @function sortListAlphabetically
   * @description Sort list alphabetically
   * @author Iago Rocha
   * @param {object[]} objListToSort
   * @param {string} strNameKey
   * @returns {object}
   */
  sortListAlphabetically (objListToSort: object[], strNameKey: string): object[] {
    return objListToSort.sort((a, b) => {
      if (a[strNameKey] < b[strNameKey]) {
        return -1
      }
      if (a[strNameKey] > b[strNameKey]) {
        return 1
      }
      return 0
    })
  }

  /**
   * @function getFirstName
   * @description Split string and return first word
   * @author Iago Rocha
   * @param {string} strFullName
   * @returns {string}
   */
  getFirstName (strFullName: string): string {
    try {
      return strFullName.split(' ')[0]
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  /**
   * @function isIos
   * @description Check if browser on IOS
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  isIos (): boolean {
    return /iphone/.test(window.navigator.userAgent.toLowerCase())
  }

  /**
   * @function isSafariBrowser
   * @description Check if is Safari Browserr
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  isSafariBrowser (): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }

  /**
   * @function getBrowserName
   * @description Get Browser name
   * @author Marcelo Melo
   * @param {void}
   * @returns {string}
   */
  getBrowserName (): string {
    const objBowser = Bowser.parse(window.navigator.userAgent)
    return objBowser.browser.name
  }

  /**
   * @function isStandaloneMode
   * @description Check if is standalone mode
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  isStandaloneMode (): boolean {
    return (window.matchMedia('(display-mode: standalone)').matches)
  }

  /**
   * @function applyBlinkEffect
   * @description Apply effect to blink on element
   * @author Iago Rocha
   * @param {HtmlElement} htmlElement
   * @param {number} intTimeBlinking (optional - default = 2000ms)
   * @returns {Promise<boolean>}
   */
  applyBlinkEffect (htmlElement: HTMLElement | Element, intTimeBlinking?: number): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        // Add class to blink element
        htmlElement.classList.add('blink')
        // Remove class to blink
        setTimeout(() => {
          htmlElement.classList.remove('blink')
          resolve(true)
        }, intTimeBlinking || 2000)
      } catch (error) {
        console.error(error)
        resolve(false)
      }
    })
  }

  /**
   * @function getLastElementByQuery
   * @description Get last element by query selector
   * @author Iago Rocha
   * @param {string} strQuerySelector
   * @returns {Element}
   */
  getLastElementByQuery (strQuerySelector: string): Element {
    const listOfElements = document.querySelectorAll(strQuerySelector)
    return listOfElements[listOfElements.length - 1]
  }

  /**
   * @function getProgressOfPosition
   * @description Calculate the circle position in percentage
   * @author Iago Rocha
   * @param {Token} token
   * @returns {object}
   */
  getProgressOfPosition (token: Token): { full: number, position: number } {
    try {
      // Get waiting time
      const intWaitingTime = this.dateTimeTools.timeDiffInFloat(token.ticket_generated_at)
      // Get diff between dates (queue_last_call_at - ticket_generated_at)
      const intDiffLastCall = this.dateTimeTools.timeDiffInFloat(token.ticket_generated_at, token.ewt_base_time)
      // Fullcircle = ((queue_last_call_at - ticket_generated_at) + ewt)
      const intFullCircle = intDiffLastCall + token.ewt
      let intCirclePosition = Math.round((intWaitingTime * 100) / intFullCircle)
      // Handle if exceed 100%
      if (intCirclePosition > 100) {
        intCirclePosition = 100
      }
      // Handle if intFullCircle less than 0
      if (intFullCircle < 1) {
        intCirclePosition = 100
      }
      // Handle if NaN
      if (isNaN(intCirclePosition)) {
        intCirclePosition = 0
      }
      // Return fullcircle and circle position
      return {
        full: intFullCircle,
        position: intCirclePosition
      }
    } catch (error) {
      console.error(error)
      return {
        full: 0,
        position: 0
      }
    }
  }
}
